var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
var getAngleFromDelta = function (deltaX, deltaY) {
    return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
};
/**
 * Because older browsers will interpret any object in the 3rd argument
 * as a true value for the capture argument, it's important for developers
 * to use feature detection or a polyfill when using this API, to avoid
 * unforeseen results.
 * Resource: https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md#feature-detection
 */
var supportsCaptureOption = false;
/* tslint:disable */
try {
    var opts = Object.defineProperty({}, 'capture', {
        // eslint-disable-next-line getter-return
        get: function () {
            supportsCaptureOption = true;
        },
    });
    window.addEventListener('test', null, opts);
    window.removeEventListener('test', null, opts);
}
catch (e) { }
var getSafeEventHandlerOpts = function (options) {
    if (options === void 0) { options = { capture: true }; }
    return (supportsCaptureOption ? options : options.capture);
};
/**
 * getPosition returns a position element that works for mouse or touch events
 *
 * @param  {[Event]} event [the received event]
 * @return {[Object]}      [x and y coords]
 */
var getPosition = function (event) {
    if ('touches' in event) {
        var _a = event.touches[0], pageX = _a.pageX, pageY = _a.pageY;
        return { x: pageX, y: pageY };
    }
    var screenX = event.screenX, screenY = event.screenY;
    return { x: screenX, y: screenY };
};
var moveStart = null;
var movePosition = null;
var moving = false;
/* tslint:disable variable-name*/
/**
 * The code for this component was inspired in react-easy-swipe
 * https://github.com/leandrowd/react-easy-swipe
 */
var SwipeListener = function (props) {
    var onSwipeStart = props.onSwipeStart, onSwipeMove = props.onSwipeMove, onSwipeEnd = props.onSwipeEnd, onSwipeUp = props.onSwipeUp, onSwipeDown = props.onSwipeDown, onSwipeLeft = props.onSwipeLeft, onSwipeRight = props.onSwipeRight;
    var _a = __read(useState(null), 2), swiperRef = _a[0], setSwiperRef = _a[1];
    useEffect(function () {
        if (swiperRef) {
            swiperRef.addEventListener('touchmove', _handleSwipeMove, getSafeEventHandlerOpts({
                capture: true,
                passive: false,
            }));
        }
        return function () {
            if (swiperRef) {
                swiperRef.removeEventListener('touchmove', _handleSwipeMove, getSafeEventHandlerOpts({
                    capture: true,
                    passive: false,
                }));
            }
        };
    }, [swiperRef]);
    var _handleSwipeStart = function (event) {
        var _a = getPosition(event), x = _a.x, y = _a.y;
        moveStart = { x: x, y: y };
        onSwipeStart(event);
    };
    var _handleSwipeMove = function (event) {
        if (!moveStart) {
            return;
        }
        var _a = getPosition(event), x = _a.x, y = _a.y;
        var deltaX = x - moveStart.x;
        var deltaY = y - moveStart.y;
        moving = true;
        // handling the responsability of cancelling the scroll to
        // the component handling the event
        var shouldPreventDefault = onSwipeMove({
            x: deltaX,
            y: deltaY,
        }, event);
        if (shouldPreventDefault) {
            event.preventDefault();
        }
        movePosition = { deltaX: deltaX, deltaY: deltaY };
    };
    var _handleSwipeEnd = function (event) {
        onSwipeEnd(event);
        if (moving && movePosition) {
            var angle = getAngleFromDelta(movePosition.deltaX, movePosition.deltaY);
            if (angle > -112.5 && angle < -67.5) {
                onSwipeUp(1, event);
            }
            if (angle > -22.5 && angle < 22.5) {
                onSwipeRight(1, event);
            }
            if (angle > 67.5 && angle < 112.5) {
                onSwipeDown(1, event);
            }
            if ((angle > 157.5 && angle < 180) || (angle > -180 && angle < -157.5)) {
                onSwipeLeft(1, event);
            }
        }
    };
    var _setSwiperRef = function (node) {
        setSwiperRef(node);
    };
    return (React.createElement("div", __assign({ ref: _setSwiperRef, onTouchStart: _handleSwipeStart, onTouchEnd: _handleSwipeEnd }, props), props.children));
};
SwipeListener.propTypes = {
    children: PropTypes.node,
    onSwipeStart: PropTypes.func,
    onSwipeMove: PropTypes.func,
    onSwipeEnd: PropTypes.func,
    onSwipeUp: PropTypes.func,
    onSwipeDown: PropTypes.func,
    onSwipeLeft: PropTypes.func,
    onSwipeRight: PropTypes.func,
};
/* tslint:disable no-empty */
SwipeListener.defaultProps = {
    onSwipeStart: function () { },
    onSwipeMove: function () { },
    onSwipeEnd: function () { },
    onSwipeUp: function () { },
    onSwipeDown: function () { },
    onSwipeLeft: function () { },
    onSwipeRight: function () { },
};
/* tslint:enable no-empty */
export default SwipeListener;
